import React, { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { apiService } from '../../services/api';
import PasswordInput from './PasswordInput';

interface FormData {
  password: string;
  confirmPassword: string;
}

const labelStyle: React.CSSProperties = {
  display: 'block',
  color: '#cccccc',
  marginBottom: '8px',
  fontSize: '14px',
  fontWeight: 500,
};

const errorTextStyle: React.CSSProperties = {
  marginTop: '6px',
  color: '#f44336',
  fontSize: '13px',
};

const passwordRules = [
  { id: 'length', label: 'At least 8 characters', test: (p: string) => p.length >= 8 },
  { id: 'letter', label: 'Contains a letter', test: (p: string) => /[A-Za-z]/.test(p) },
  { id: 'number', label: 'Contains a number', test: (p: string) => /\d/.test(p) },
];

const ResetPassword: React.FC = () => {
  const { token } = useParams<{ token: string }>();
  const navigate = useNavigate();
  const {
    register,
    handleSubmit,
    watch,
    setFocus,
    formState: { errors },
  } = useForm<FormData>({ mode: 'onTouched', defaultValues: { password: '', confirmPassword: '' } });
  const [serverError, setServerError] = useState<string>('');
  const [isLoading, setIsLoading] = useState(false);

  const passwordValue = watch('password') || '';

  useEffect(() => {
    setFocus('password');
  }, [setFocus]);

  const onSubmit = async (data: FormData) => {
    if (!token) {
      setServerError('This reset link is missing a token.');
      return;
    }
    setIsLoading(true);
    setServerError('');
    try {
      await apiService.resetPassword(token, data.password);
      navigate('/login', {
        replace: true,
        state: { resetSuccess: true },
      });
    } catch (err: any) {
      const detail = err?.response?.data?.detail;
      setServerError(
        (typeof detail === 'object' && detail?.message) ||
          (typeof detail === 'string' && detail) ||
          'Something went wrong. The link may have expired.'
      );
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="app-layout min-h-screen flex items-center justify-center py-12 px-4">
      <div className="max-w-md w-full">
        <div className="text-center" style={{ marginBottom: '24px' }}>
          <div className="app-brand" style={{ fontSize: '32px', marginBottom: '16px' }}>
            babylon.markets
          </div>
          <h2 style={{ color: '#ffffff', fontSize: '24px' }}>Choose a new password</h2>
        </div>
        <div className="app-card">
          <form onSubmit={handleSubmit(onSubmit)} noValidate className="space-y-6">
            <div>
              <label htmlFor="new-password" style={labelStyle}>
                New password
              </label>
              <PasswordInput
                id="new-password"
                autoComplete="new-password"
                placeholder="Enter a new password"
                invalid={!!errors.password}
                aria-describedby="new-password-rules"
                {...register('password', {
                  required: 'Password is required',
                  validate: (v) => {
                    if (v.length < 8) return 'At least 8 characters';
                    if (!/[A-Za-z]/.test(v)) return 'Must contain a letter';
                    if (!/\d/.test(v)) return 'Must contain a number';
                    return true;
                  },
                })}
              />
              <ul
                id="new-password-rules"
                style={{ margin: '8px 0 0', padding: 0, listStyle: 'none', fontSize: '12px' }}
              >
                {passwordRules.map((rule) => {
                  const ok = rule.test(passwordValue);
                  return (
                    <li
                      key={rule.id}
                      style={{
                        color: ok ? '#22c55e' : '#888888',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '6px',
                        lineHeight: 1.6,
                      }}
                    >
                      <span aria-hidden="true">{ok ? '✓' : '○'}</span>
                      {rule.label}
                    </li>
                  );
                })}
              </ul>
              {errors.password && <p style={errorTextStyle}>{errors.password.message}</p>}
            </div>

            <div>
              <label htmlFor="confirm-new-password" style={labelStyle}>
                Confirm new password
              </label>
              <PasswordInput
                id="confirm-new-password"
                autoComplete="new-password"
                placeholder="Re-enter the password"
                invalid={!!errors.confirmPassword}
                {...register('confirmPassword', {
                  required: 'Please confirm your password',
                  validate: (v) => v === passwordValue || 'Passwords do not match',
                })}
              />
              {errors.confirmPassword && (
                <p style={errorTextStyle}>{errors.confirmPassword.message}</p>
              )}
            </div>

            {serverError && (
              <div
                role="alert"
                aria-live="polite"
                style={{
                  background: 'rgba(244, 67, 54, 0.08)',
                  border: '1px solid rgba(244, 67, 54, 0.4)',
                  color: '#f88',
                  padding: '10px 12px',
                  borderRadius: '4px',
                  fontSize: '13px',
                  textAlign: 'center',
                }}
              >
                {serverError}
              </div>
            )}

            <button
              type="submit"
              disabled={isLoading}
              className="btn-primary"
              style={{
                width: '100%',
                opacity: isLoading ? 0.5 : 1,
                cursor: isLoading ? 'not-allowed' : 'pointer',
              }}
            >
              {isLoading ? 'Updating...' : 'Update password'}
            </button>

            <p style={{ textAlign: 'center', fontSize: '13px' }}>
              <Link to="/login" style={{ color: '#2962ff' }}>
                Back to sign in
              </Link>
            </p>
          </form>
        </div>
      </div>
    </div>
  );
};

export default ResetPassword;
